import React from "react";

import classNames from "classnames";
import { renderToStaticMarkup } from "react-dom/server";
import { v4 as uuidv4 } from "uuid";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import type { JSX } from "react";

export interface PercentageItem {
  value: number;
  tooltip?: string | JSX.Element;
}

interface PercentageProps extends PercentageItem {
  percentageTotal: number;
}

function Percentage({
  value,
  tooltip,
  percentageTotal,
}: PercentageProps): JSX.Element {
  const progressBarClass = classNames(
    "h-full ease-in-out duration-500 rounded-[0.234375rem]",
    {
      "bg-status-active": percentageTotal >= 90 && percentageTotal <= 100,
      "bg-[#EFD33F]": percentageTotal > 25 && percentageTotal < 90,
      "bg-destructive": percentageTotal <= 25 || percentageTotal > 100,
    },
  );

  const getWidth = (): string => {
    if (value > 100) {
      return "100%";
    }

    return `${value}%`;
  };

  if (!tooltip) {
    return (
      <div
        className={progressBarClass}
        style={{
          width: getWidth(),
        }}
      />
    );
  }

  const tooltipContent =
    typeof tooltip === "string" ? <span>{tooltip}</span> : tooltip;

  return (
    <div
      className={progressBarClass}
      data-tooltip-html={renderToStaticMarkup(tooltipContent)}
      data-tooltip-id={APP_TOOLTIP_ID}
      data-tooltip-place="bottom"
      style={{
        width: getWidth(),
      }}
    />
  );
}

interface ProgressBarProps {
  percentageList: PercentageItem[];
}

function ProgressBar({ percentageList }: ProgressBarProps): JSX.Element {
  const totalPercentage = percentageList.reduce(
    (acc, percentage) => acc + percentage.value,
    0,
  );

  return (
    <div className="flex w-full items-center gap-[0.78125rem]">
      <div className="border-input flex h-[1.40625rem] flex-1 items-center rounded-[0.234375rem] border">
        <div className="relative flex h-full flex-1 items-center gap-px p-[3px]">
          {percentageList.map((percentage) => (
            <Percentage
              key={uuidv4()}
              percentageTotal={totalPercentage}
              tooltip={percentage.tooltip}
              value={percentage.value}
            />
          ))}
          <span className="absolute left-1/2 -translate-x-1/2 text-[0.78125rem] font-semibold">
            {Math.floor(totalPercentage)}%
          </span>
        </div>
      </div>
    </div>
  );
}

export default ProgressBar;
